/**
 * Safety model shared by the seed pipeline, the API (on every admin edit) and the app.
 * Recipe safety is always *derived* from ingredients so it can never drift out of date.
 */

export type Ingestion = "culinary" | "caution" | "do_not_ingest";
export type PetStatus = "toxic" | "non_toxic" | "not_listed";
export type OilPetStatus = "avoid" | "caution";

export interface PlantSafety {
  ingestion: Ingestion;
  human_cautions: string[];
  pregnancy_caution: boolean;
  pets: { cats: PetStatus; dogs: PetStatus; horses: PetStatus };
}

export interface OilSafety {
  ingestion: Ingestion;
  human_cautions: string[];
  pregnancy_caution?: boolean;
  phototoxic?: boolean;
  pets?: { cats: OilPetStatus; dogs: OilPetStatus };
}

export interface CrystalCare {
  water_safe: boolean;
  sun_safe: boolean;
  salt_safe: boolean;
  toxic_handling: boolean;
}

export type ResolvedIngredient =
  | { kind: "plant"; name: string; safety: PlantSafety }
  | { kind: "oil"; name: string; oilType: "essential" | "carrier"; safety: OilSafety }
  | { kind: "crystal"; name: string; care: CrystalCare }
  | { kind: "other"; name: string };

/** How a recipe is used. Drink/eat recipes must only contain culinary ingredients. */
export type RecipeUse =
  | "burn"
  | "drink"
  | "eat"
  | "bathe"
  | "anoint"
  | "carry"
  | "keep"
  | "bury_or_keep"
  | "sprinkle"
  | "wash"
  | "simmer"
  | "various";

const EDIBLE_USES: readonly RecipeUse[] = ["drink", "eat"];
const WATER_TYPES = ["bath", "water", "tea"];

export interface RecipeSafety {
  ingestible: boolean;
  pet_warnings: { cats: string[]; dogs: string[] };
  pet_friendly: boolean;
  pregnancy_caution_ingredients: string[];
  human_cautions: string[];
  derived: true;
}

export interface RecipeSafetyResult {
  safety: RecipeSafety;
  /** Blocking problems. A recipe with problems must not be published. */
  problems: string[];
}

export function isEdibleUse(use: string): boolean {
  return (EDIBLE_USES as readonly string[]).includes(use);
}

/** Derive a recipe's safety summary and any blocking problems from its ingredients. */
export function deriveRecipeSafety(
  recipe: { use: string; type: string },
  ingredients: ResolvedIngredient[],
): RecipeSafetyResult {
  const cats = new Set<string>();
  const dogs = new Set<string>();
  const pregnancy = new Set<string>();
  const human: string[] = [];
  const problems: string[] = [];
  const edible = isEdibleUse(recipe.use);

  for (const ing of ingredients) {
    switch (ing.kind) {
      case "plant": {
        const s = ing.safety;
        if (edible && s.ingestion !== "culinary") {
          problems.push(`${ing.name} is marked "${s.ingestion}" and cannot be in an edible recipe`);
        }
        if (s.pets.cats === "toxic") cats.add(ing.name);
        if (s.pets.dogs === "toxic") dogs.add(ing.name);
        if (s.pregnancy_caution) pregnancy.add(ing.name);
        human.push(...s.human_cautions.map((c) => `${ing.name}: ${c}`));
        break;
      }
      case "oil": {
        const s = ing.safety;
        if (edible && s.ingestion !== "culinary") {
          problems.push(`${ing.name} is not safe to ingest`);
        }
        if (ing.oilType === "essential") {
          cats.add(ing.name);
          if (s.pets?.dogs === "avoid") dogs.add(ing.name);
          if (s.pregnancy_caution) pregnancy.add(ing.name);
          if (s.phototoxic) human.push(`${ing.name}: phototoxic, avoid sun on treated skin`);
        }
        break;
      }
      case "crystal": {
        const unsafeInWater = !ing.care.water_safe || ing.care.toxic_handling;
        if (WATER_TYPES.includes(recipe.type) && unsafeInWater) {
          problems.push(`${ing.name} is not water safe`);
        }
        break;
      }
      case "other":
        break;
    }
  }

  const sorted = (s: Set<string>) => [...s].sort();
  return {
    safety: {
      ingestible: edible && problems.length === 0,
      pet_warnings: { cats: sorted(cats), dogs: sorted(dogs) },
      pet_friendly: cats.size === 0 && dogs.size === 0,
      pregnancy_caution_ingredients: sorted(pregnancy),
      human_cautions: human,
      derived: true,
    },
    problems,
  };
}

/** A plant is pet friendly only when ASPCA lists it non-toxic for both cats and dogs. */
export function isPetFriendlyPlant(safety: PlantSafety): boolean {
  return safety.pets.cats === "non_toxic" && safety.pets.dogs === "non_toxic";
}

export type SafetyBadge = { tone: "danger" | "warning" | "info" | "ok"; label: string };

/** Short, human-readable badges for any item's safety block. Danger first. */
export function safetyBadges(
  item:
    | { category: "plant"; safety: PlantSafety }
    | { category: "oil"; safety: OilSafety }
    | { category: "crystal"; care: CrystalCare },
): SafetyBadge[] {
  const out: SafetyBadge[] = [];
  if (item.category === "crystal") {
    if (item.care.toxic_handling) out.push({ tone: "danger", label: "Toxic dust: wash hands" });
    if (!item.care.water_safe) out.push({ tone: "warning", label: "Keep dry" });
    if (!item.care.sun_safe) out.push({ tone: "info", label: "Fades in sun" });
    if (!item.care.salt_safe) out.push({ tone: "info", label: "No salt cleansing" });
    return out;
  }
  const s = item.safety;
  if (s.ingestion === "do_not_ingest") out.push({ tone: "danger", label: "Do not ingest" });
  if (s.ingestion === "caution") out.push({ tone: "warning", label: "Use with caution" });
  if (s.pregnancy_caution) out.push({ tone: "warning", label: "Pregnancy caution" });
  if (item.category === "plant") {
    const { cats, dogs } = item.safety.pets;
    if (cats === "toxic" || dogs === "toxic") out.push({ tone: "danger", label: "Toxic to pets" });
    else if (isPetFriendlyPlant(item.safety)) out.push({ tone: "ok", label: "Pet friendly" });
  } else if (item.safety.pets) {
    out.push({ tone: "danger", label: "Avoid around cats" });
    if (item.safety.phototoxic) out.push({ tone: "warning", label: "Phototoxic" });
  }
  return out;
}
